import { Component, Renderer2 } from '@angular/core';
import { DataTableBase } from '../../../../core/base/components/data-table-base/data-table-base';
import { CONTRACT_FILTER_SCHEMA } from './contract-filter/contract-filter.schema';
import { ContractModel } from '../../../../core/models/contract.model';
import { ContractService } from './contract.service';
import { BUBBLE_COLOR_ENUM, bubbleColor, FeiColumn } from '../../../../core/base/interfaces/fei-column.interface';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { ContractStatusEnum, ContractStatusTranslation } from '../../../../core/enums/contract-status.enum';
import { GenericEngagementFormatEnum } from '../../../../core/enums/generic-engagement-format.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { NotificationService } from '../../../../core/layout/application-layout/notification/notification.service';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { ContractCreateUpdateComponent } from './contract-create-update/contract-create-update.component';
import { ContractArchiveDeleteComponent } from './modals/contract-archive-delete/contract-archive-delete.component';
import { DependenciesService } from '../../../../core/services/dependencies.service';
import { CrudModeEnum } from '../../../../core/base/enum/crud-mode.enum';

@Component({
  selector: 'vex-contract',
  templateUrl: './contract.component.html',
  styleUrls: ['./contract.component.scss'],
})
export class ContractComponent extends DataTableBase<ContractModel> {
  entityName = 'Engagements';
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  pageSize = 50;
  protected override filterSchema = CONTRACT_FILTER_SCHEMA;
  /** Vue par défaut : engagements en cours uniquement (les échus sont accessibles via la case Archivés). */
  private static readonly DEFAULT_STATUSES = [ContractStatusEnum.DRAFT, ContractStatusEnum.VALIDATED];

  displayActionColumn = (contract: ContractModel): boolean =>
    !!contract.supportEstablishment && contract.status === ContractStatusEnum.VALIDATED;

  // Système de filtrage par type d'engagement (radio buttons)
  currentCheckedValue: any = null;
  public readonly engagementTypes = [
    { label: 'Contrat', value: 'CONTRACT' },
    { label: 'Devis', value: 'QUOTE' },
  ];
  GenericEngagementFormatEnum = GenericEngagementFormatEnum;

  feisColumns: Array<FeiColumn> = [
    // {label: 'ID Technique', column: 'technicalIdentifier', propertyDisplay: null},
    // {label: 'ID GED contrat', column: 'referenceInterne', propertyDisplay: null},
    // {
    //     label: 'Catégorie',
    //     column: 'genericEngagement.engagementCategory',
    //     propertyDisplay: 'genericEngagement.engagementCategory.label'
    // },
    { label: "Type d'engagement", column: 'genericEngagement', propertyDisplay: 'displayFormat' },
    { label: 'Objet', column: 'object', propertyDisplay: 'object' },
    { label: 'Fournisseur', column: 'supplier', propertyDisplay: 'supplier.label' },
    // {
    //     label: 'Réf contrat frs',
    //     column: 'supplierContractReference.',
    //     propertyDisplay: 'supplierContractReference'
    // },
    { label: 'Date de début', column: 'startValidityDate', propertyDisplay: 'startValidityDate', type: 'date' },
    {
      label: 'Date révision prix',
      column: 'nextRevisionDate',
      propertyDisplay: 'priceRevision.nextRevisionDate',
      type: 'date',
      statusClassFunc: this.displayRenewalDateStatus.bind(this),
    },
    {
      label: 'Date de préavis',
      column: 'noticeDate',
      type: 'date',
      statusClassFunc: this.displayNoticeDateStatus.bind(this),
    },
    {
      label: 'Date de fin',
      column: 'endValidityDate',
      propertyDisplay: 'endValidityDate',
      type: 'date',
      statusClassFunc: this.displayEndValidityDateStatus.bind(this),
    },
    // {label: 'Conditions particulières', column: 'specialConditions', type: 'isSpecialConditions'},
    {
      label: 'Statut',
      column: 'status',
      propertyDisplay: 'displayStatus',
      statusClassFunc: this.displayStatusField,
    },
    { label: 'Référent engagement', column: 'engagementContact', propertyDisplay: 'engagementContact.fullName' },
    {
      label: '',
      column: 'actions',
      showAction: true,
      canUpdate: false,
      canArchive: true,
      showActionDynamically: this.displayActionColumn,
    },
  ];

  legendData = [
    { description: 'Échéances proches', color: '#FFC0CB' }, // Rose clair
  ];

  constructor(
    public service: ContractService,
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
    public notificationService: NotificationService,
    private ren: Renderer2,
    private dependencyService: DependenciesService<ContractModel>,
  ) {
    super();
  }

  /** Une fois, après l'hydratation depuis l'URL : la radio Contrat/Devis reflète l'état restauré. */
  protected override setupTable(): void {
    this.currentCheckedValue = this.filters.engagementType ?? null;
  }

  /**
   * Payload dérivé de l'état utilisateur, hors `this.filters` (donc hors URL) :
   * - case Archivés → `isExpiredOrArchived` (le back n'utilise pas `isArchive` pour les engagements) ;
   * - hors mode archivés, sans statut choisi → statuts par défaut ;
   * - radio Contrat/Devis → `genericEngagementFormat` / `genericEngagementFormatExclude`.
   */
  protected override buildFilters(): Record<string, unknown> {
    const { isArchive, isExpired, engagementType, ...userFilters } = super.buildFilters() as any;
    const derived: Record<string, unknown> = {};

    if (this.isArchive) {
      derived.isExpiredOrArchived = true;
    } else if (!userFilters.status?.length) {
      derived.status = ContractComponent.DEFAULT_STATUSES;
    }

    if (engagementType === GenericEngagementFormatEnum.QUOTE) {
      derived.genericEngagementFormat = [GenericEngagementFormatEnum.QUOTE];
    } else if (engagementType === 'CONTRACT') {
      derived.genericEngagementFormatExclude = [GenericEngagementFormatEnum.QUOTE];
    }

    return { ...userFilters, ...derived };
  }

  /** La radio Contrat/Devis vit hors du sidenav : conservée quand celui-ci applique ou efface. */
  protected override getPreservedFilters(): any {
    return this.filters.engagementType ? { engagementType: this.filters.engagementType } : {};
  }

  /** Le badge du bouton Filtres ne compte que le sidenav, pas la radio. */
  protected override countActiveFilters(filters: Record<string, unknown> = this.filters): number {
    return super.countActiveFilters({ ...filters, engagementType: undefined });
  }

  protected override transformData(data: any[]): any[] {
    // Traduire les statuts uniquement
    // Le tri par statut est géré côté backend pour permettre le tri par colonnes
    return data.map((contract: ContractModel) => {
      contract.displayStatus = ContractStatusTranslation.translate(contract.status);

      const baseFormat = contract?.genericEngagement?.format as unknown as GenericEngagementFormatEnum;
      if (baseFormat === GenericEngagementFormatEnum.CONTRACT && contract?.isInternal) {
        (contract as any).displayFormat = GenericEngagementFormatEnum.INTERNE;
      } else {
        (contract as any).displayFormat = baseFormat;
      }

      return contract;
    });
  }

  displayStatusField(element: ContractModel): bubbleColor {
    switch (element.status) {
      case ContractStatusEnum.DRAFT:
        return 'yellow';
      default:
        return '';
    }
  }

  displayRenewalDateStatus(element: ContractModel): bubbleColor {
    return this.compareDateWithCurrent(element.priceRevision.nextRevisionDate, 3)
      ? BUBBLE_COLOR_ENUM.RED
      : BUBBLE_COLOR_ENUM.EMPTY;
  }

  displayNoticeDateStatus(element: ContractModel): bubbleColor {
    return this.compareDateWithCurrent(element.noticeDate, 3) ? BUBBLE_COLOR_ENUM.RED : BUBBLE_COLOR_ENUM.EMPTY;
  }

  displayEndValidityDateStatus(element: ContractModel): bubbleColor {
    return this.compareDateWithCurrent(element.endValidityDate, 3) ? BUBBLE_COLOR_ENUM.RED : BUBBLE_COLOR_ENUM.EMPTY;
  }

  compareDateWithCurrent(inputDate: Date | string, months: number, days: number = 0): boolean {
    if (!inputDate) {
      return false;
    }

    const date = typeof inputDate === 'string' ? new Date(inputDate) : inputDate;
    const currentDate = new Date();

    if (isNaN(date.getTime()) || date.getTime() < currentDate.getTime()) {
      return false;
    }

    const yearDifference = currentDate.getFullYear() - date.getFullYear();
    const monthDifference = currentDate.getMonth() - date.getMonth();
    const totalMonthDifference = yearDifference * 12 + monthDifference;

    const dayDifference = currentDate.getDate() - date.getDate();

    if (Math.abs(totalMonthDifference) > months) {
      return false;
    }

    return !(Math.abs(totalMonthDifference) === months && dayDifference > days);
  }

  createItem(): void {
    const element = this.service.entity;
    this.dialog
      .open(ContractCreateUpdateComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Create,
        },
      })
      .afterClosed()
      .subscribe(result => {
        console.log(result);
        if (result) {
          this.router.navigate(['/management/engagement/', result.data._id]);
        }
      });
  }

  async archiveItem(element: ContractModel) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = element.endValidityDate ? new Date(element.endValidityDate) : today;
    const isActive = endDate && endDate >= today;

    if (
      element.status === ContractStatusEnum.VALIDATED &&
      element.supportEstablishment &&
      element.isArchive === false &&
      isActive
    ) {
      this.dialog
        .open(ContractArchiveDeleteComponent, {
          data: { defaults: element },
        })
        .afterClosed()
        .subscribe(res => {
          if (res) {
            this.initData();
          }
        });
    } else {
      return;
    }
  }

  /**
   * Gère l'état des radio buttons - permet de décocher en cliquant à nouveau
   * Utilise le même pattern que SupplierComponent
   */
  checkState(el: any) {
    setTimeout(() => {
      if (this.currentCheckedValue && this.currentCheckedValue === el.value) {
        // Si le bouton est déjà sélectionné, on le décoche
        el.checked = false;
        this.ren.removeClass(el._elementRef.nativeElement, 'cdk-focused');
        this.ren.removeClass(el._elementRef.nativeElement, 'cdk-program-focused');
        this.currentCheckedValue = null;
        this.setEngagementType(null);
      } else {
        // Sinon on sélectionne le nouveau bouton
        this.currentCheckedValue = el.value;
        this.setEngagementType(el.value);
      }
    });
  }

  /** Radio Contrat/Devis : 'CONTRACT' = tout sauf devis, 'QUOTE' = devis uniquement, null = tout. */
  private setEngagementType(value: 'CONTRACT' | 'QUOTE' | null) {
    if (value) {
      this.filters.engagementType = value;
    } else {
      delete this.filters.engagementType;
    }
    this.pageIndex = 1;
    this.initData();
  }
}
